/**
 * DealerTermsPage — Händler-Nutzungs-/Lizenzvertrag (SaaS / B2B).
 * Software-Lizenz, IP, Datenhoheit, Entgelte, SLA, Haftung, Laufzeit/Kündigung.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileSignature, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const Section = ({ title, children }) => (
    <section className="space-y-1">
        <h2 className="font-display text-lg font-bold">{title}</h2>
        <div className="text-sm text-gray-700 space-y-1">{children}</div>
    </section>
);

export default function DealerTermsPage() {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);
    const load = () => api.get("/privacy/dealer-terms").then(({ data }) => setData(data)).catch(() => setData({}));
    useEffect(() => { load(); }, []);

    const accept = async () => {
        setSaving(true);
        try { await api.post("/privacy/dealer-terms/accept"); toast.success("Nutzungsbedingungen akzeptiert."); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Bitte als Händler einloggen."); }
        finally { setSaving(false); }
    };

    if (!data) return null;

    return (
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-10" data-testid="dealer-terms-page">
            <Link to={-1} onClick={(e) => { e.preventDefault(); window.history.back(); }} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-[#111827] mb-4">
                <ArrowLeft className="w-4 h-4" /> Zurück
            </Link>
            <div className="flex items-center gap-3 mb-2">
                <FileSignature className="w-7 h-7 text-[#FFCC00]" />
                <h1 className="font-display text-3xl font-black tracking-tight">Händler-Nutzungsbedingungen (SaaS-Lizenz)</h1>
            </div>
            <p className="text-sm text-gray-500 mb-6">Vertrag zwischen Händler und twowheels.info · Version {data.version}</p>

            <article className="space-y-5">
                <Section title="1. Lizenz & Nutzungsrecht">
                    <p>twowheels.info räumt dem Händler für die Vertragslaufzeit ein einfaches, nicht ausschließliches, nicht übertragbares Recht zur Nutzung der Plattform (SaaS) für die eigene Geschäftstätigkeit ein. Eine Weitergabe der Zugänge an Dritte ist unzulässig.</p>
                </Section>
                <Section title="2. Geistiges Eigentum">
                    <p>Sämtliche Rechte an Software, Quellcode, Design und Marken verbleiben bei twowheels.info. Der Händler erhält keine Eigentums-, sondern ausschließlich Nutzungsrechte.</p>
                </Section>
                <Section title="3. Datenhoheit">
                    <p>Die vom Händler eingebrachten Geschäfts- und Kundendaten verbleiben im Eigentum des Händlers (Verantwortlicher i. S. d. DSGVO). twowheels.info verarbeitet diese ausschließlich gemäß dem separaten Auftragsverarbeitungsvertrag (AVV).</p>
                </Section>
                <Section title="4. Entgelte & Abrechnung">
                    <p>Die Nutzung erfolgt zu den jeweils vereinbarten Konditionen. Nutzungsabhängige Entgelte werden gemäß der hinterlegten Preisliste abgerechnet (z. B. SEPA-Nutzungsabrechnung).</p>
                </Section>
                <Section title="5. Verfügbarkeit (SLA)">
                    <p>twowheels.info bemüht sich um eine hohe Verfügbarkeit. Wartungsfenster und höhere Gewalt sind ausgenommen. Es besteht kein Anspruch auf 100 % Verfügbarkeit.</p>
                </Section>
                <Section title="6. Haftung">
                    <p>Die Haftung ist – außer bei Vorsatz, grober Fahrlässigkeit oder Verletzung von Leben, Körper, Gesundheit – auf den vertragstypischen, vorhersehbaren Schaden begrenzt.</p>
                </Section>
                <Section title="7. Laufzeit, Kündigung & Datenexport">
                    <p>Der Vertrag läuft auf unbestimmte Zeit und ist mit angemessener Frist kündbar. Nach Vertragsende stellt twowheels.info dem Händler seine Daten in einem gängigen Format zur Verfügung (Datenexport) und löscht sie anschließend nach Ablauf gesetzlicher Aufbewahrungsfristen.</p>
                </Section>
                <Section title="8. White-Label / Unterlizenzen">
                    <p>Eine White-Label-Nutzung oder Unterlizenzierung bedarf einer gesonderten Vereinbarung.</p>
                </Section>
                <p className="text-xs text-gray-400">Hinweis: Mustertext – vor Verwendung anwaltlich prüfen lassen.</p>
            </article>

            <div className="mt-8 rounded-xl border border-gray-200 bg-white p-5">
                {data.accepted ? (
                    <div className="flex items-center gap-2 text-emerald-700 font-bold" data-testid="dealer-terms-accepted">
                        <CheckCircle2 className="w-5 h-5" /> Akzeptiert {data.accepted_at ? `am ${new Date(data.accepted_at).toLocaleDateString("de-DE")}` : ""} (Version {data.accepted_version})
                    </div>
                ) : (
                    <>
                        <p className="text-sm text-gray-600 mb-3">Mit Klick bestätigen Sie die Händler-Nutzungsbedingungen in Version {data.version} rechtsverbindlich (Nachweis mit Zeitstempel).</p>
                        <button onClick={accept} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-black disabled:opacity-60" data-testid="dealer-terms-accept-btn">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSignature className="h-4 w-4" />} Nutzungsbedingungen akzeptieren
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
